import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';
import { theme, spacing, typography, buttonStyles, mergeStyles, isMobile } from '../styles/theme';
import { useEffect } from 'react';
import SimpleHeader from '../components/SimpleHeader';

export default function Settings({ user, userData }) {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Display name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(user?.displayName || '');
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    const wasCancelled = sessionStorage.getItem('subscriptionCancelled');
    const wasReactivated = sessionStorage.getItem('subscriptionReactivated');

    if (wasCancelled === 'true') {
      setSuccess('Your subscription has been cancelled. You\'ll retain Premium access until the end of your billing period.');
      sessionStorage.removeItem('subscriptionCancelled');
    } else if (wasReactivated === 'true') {
      setSuccess('Subscription reactivated! Your Premium access will continue.');
      sessionStorage.removeItem('subscriptionReactivated');
    }
  }, []);

  // Handle display name update
  const handleUpdateDisplayName = async () => {
    if (!newDisplayName.trim()) {
      setError('Display name cannot be empty');
      return;
    }

    setIsSavingName(true);
    setError('');
    setSuccess('');

    try {
      const token = await user.getIdToken();
      
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/user/display-name`,
        { displayName: newDisplayName.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess('Display name updated successfully!');
      setIsEditingName(false);
      
      // Refresh the page to update the display name everywhere
      setTimeout(() => window.location.reload(), 1500);

    } catch (err) {
      console.error('Error updating display name:', err);
      setError(err.response?.data?.message || 'Failed to update display name');
    } finally {
      setIsSavingName(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setError('');
    setSuccess('');

    try {
      const token = await user.getIdToken();

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cancel-subscription`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      sessionStorage.setItem('subscriptionCancelled', 'true');
      window.location.reload();

    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.response?.data?.message || 'Failed to cancel subscription');
      setIsCancelling(false);
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteInput.toLowerCase() !== 'delete') {
      setError('Please type "DELETE" to confirm');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      const token = await user.getIdToken();

      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/user/account`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Account deleted:', response.data);

      await signOut(auth);
      navigate('/');
      alert('Your account has been successfully deleted.');

    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  // If not logged in, redirect
  if (!user) {
    return (
        <div style={{
            backgroundColor: theme.bg.primary,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: spacing.xl,
        }}>
            <div style={{
            textAlign: 'center',
            maxWidth: '400px',
            }}>
            <h2 style={{
                color: theme.text.primary,
                marginBottom: spacing.md,
            }}>
                Not Logged In
            </h2>
            <p style={{
                color: theme.text.secondary,
                marginBottom: spacing.lg,
            }}>
                You must be logged in to access settings.
            </p>
            <button
                onClick={() => navigate('/')}
                style={mergeStyles(buttonStyles.base, buttonStyles.primary)}
            >
                Go Home
            </button>
            </div>
        </div>
        );
    }

    if (!userData) {
        return (
        <div style={{
            backgroundColor: theme.bg.primary,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: spacing.xl,
        }}>
            <div style={{
                textAlign: 'center',
                color: theme.text.primary,
            }}>
                <div className='loading-spinner'></div>
                <p style={{ marginTop: spacing.md }}>Loading settings...</p>
            </div>
        </div>
        );
    }

    return (
        <>
            <SimpleHeader />
            <div style={{
                backgroundColor: theme.bg.primary,
                position: 'fixed',
                top: '72px',
                left: 0,
                right: 0,
                bottom: 0,
                overflowY: 'auto',
                padding: spacing['3xl'],
                ...(isMobile() && {
                    top: '70px',
                    padding: spacing.lg,
                })
            }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                backgroundColor: 'white',
                padding: spacing['3xl'],
                borderRadius: '12px',
                boxShadow: theme.shadow.medium,
                ...(window.innerWidth <= 768 && {
                padding: spacing.lg,
                })
            }}>
                <div style={{ marginBottom: spacing.lg }}>
                <a 
                    href="/" 
                    style={{
                    color: theme.brand.primary,
                    textDecoration: 'none',
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.medium,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    }}
                >
                    ← Back to Judgy
                </a>
                </div>
                {/* Header */}
                <h1 style={{
                color: theme.text.primary,
                fontSize: typography.fontSize['3xl'],
                fontWeight: typography.fontWeight.bold,
                marginBottom: spacing.lg,
                fontFamily: typography.fontFamily.primary,
                }}>
                Account Settings
                </h1>

                {/* Success/Error Messages */}
                {success && (
                <div style={{
                    backgroundColor: '#d1fae5',
                    border: '2px solid #10b981',
                    color: '#065f46',
                    padding: spacing.md,
                    borderRadius: '8px',
                    marginBottom: spacing.lg,
                }}>
                    {success}
                </div>
                )}

                {error && !showDeleteConfirm && !showCancelConfirm && (
                <div style={{
                    backgroundColor: '#fee2e2',
                    border: '2px solid #ef4444',
                    color: '#991b1b',
                    padding: spacing.md,
                    borderRadius: '8px',
                    marginBottom: spacing.lg,
                }}>
                    {error}
                </div>
                )}

                {/* Account Info */}
                <section style={{ marginBottom: spacing['3xl'] }}>
                <h2 style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.bold,
                    marginBottom: spacing.md,
                    color: theme.brand.primary,
                }}>
                    Account Information
                </h2>

                <div style={{
                    backgroundColor: theme.bg.secondary,
                    padding: spacing.lg,
                    borderRadius: '8px',
                    marginBottom: spacing.md,
                }}>
                    <div style={{ marginBottom: spacing.md }}>
                    <strong style={{ color: theme.text.primary }}>Email:</strong>{' '}
                    <span style={{ color: theme.text.secondary }}>{user.email}</span>
                    </div>

                    {/* Display Name with Edit */}
                    <div style={{ marginBottom: spacing.md }}>
                    <strong style={{ color: theme.text.primary }}>Display Name:</strong>{' '}
                    {!isEditingName ? (
                        <>
                        <span style={{ color: theme.text.secondary }}>
                            {user.displayName || 'Not set'}
                        </span>
                        <button
                            onClick={() => {
                            setIsEditingName(true);
                            setNewDisplayName(user.displayName || '');
                            setError('');
                            setSuccess('');
                            }}
                            style={{
                            marginLeft: spacing.md,
                            padding: `${spacing.xs} ${spacing.sm}`,
                            backgroundColor: 'transparent',
                            border: `2px solid ${theme.brand.primary}`,
                            color: theme.brand.primary,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            fontWeight: typography.fontWeight.medium,
                            }}
                        >
                            Edit
                        </button>
                        </>
                    ) : (
                        <div style={{ marginTop: spacing.sm }}>
                        <input
                            type="text"
                            value={newDisplayName}
                            onChange={(e) => setNewDisplayName(e.target.value)}
                            placeholder="Enter display name"
                            maxLength={50}
                            disabled={isSavingName}
                            style={{
                            width: '100%',
                            padding: spacing.sm,
                            marginBottom: spacing.sm,
                            border: `2px solid ${theme.border.medium}`,
                            borderRadius: '6px',
                            fontSize: typography.fontSize.base,
                            fontFamily: typography.fontFamily.primary,
                            }}
                        />
                        <div style={{ display: 'flex', gap: spacing.sm }}>
                            <button
                            onClick={handleUpdateDisplayName}
                            disabled={isSavingName}
                            style={mergeStyles(
                                buttonStyles.base,
                                buttonStyles.primary,
                                { fontSize: typography.fontSize.sm }
                            )}
                            >
                            {isSavingName ? 'Saving...' : 'Save'}
                            </button>
                            <button
                            onClick={() => {
                                setIsEditingName(false);
                                setNewDisplayName(user.displayName || '');
                                setError('');
                            }}
                            disabled={isSavingName}
                            style={mergeStyles(
                                buttonStyles.base,
                                buttonStyles.secondary,
                                { fontSize: typography.fontSize.sm }
                            )}
                            >
                            Cancel
                            </button>
                        </div>
                        </div>
                    )}
                    </div>

                    <div>
                    <strong style={{ color: theme.text.primary }}>Account Status:</strong>{' '}
                    <span style={{
                        color: theme.brand.primary,
                        fontWeight: typography.fontWeight.semibold,
                    }}>
                        {user.isPremium ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <img 
                                src="/premium-badge.svg" 
                                alt="Premium" 
                                style={{ height: '16px', width: '16px' }}
                                />
                                Premium
                            </span>
                            ) : 'Free'}
                    </span>
                    </div>
                </div>
                </section>

                {/* Premium Status or Upgrade */}
                {userData.isPremium ? (
                // Manage Subscription Section for Premium Users
                <section style={{ marginBottom: spacing['3xl'] }}>
                    <h2 style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.bold,
                    marginBottom: spacing.md,
                    color: theme.brand.primary,
                    }}>
                    Manage Subscription
                    </h2>

                    {userData?.subscription?.cancelAtPeriodEnd && (
                    <div style={{
                        backgroundColor: '#fff3cd',
                        border: '2px solid #ffc107',
                        padding: spacing.lg,
                        borderRadius: '8px',
                        marginBottom: spacing.lg,
                    }}>
                        <p style={{
                        color: '#856404',
                        fontWeight: typography.fontWeight.semibold,
                        marginBottom: spacing.sm,
                        fontSize: typography.fontSize.base,
                        }}>
                        ⚠️ Subscription Scheduled for Cancellation
                        </p>
                        <p style={{
                        color: '#856404',
                        fontSize: typography.fontSize.sm,
                        lineHeight: typography.lineHeight.relaxed,
                        }}>
                        Your subscription will end on{' '}
                        <strong>
                            {userData.subscription.currentPeriodEnd
                                ? new Date(userData.subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })
                                : 'the end of the current billing period'
                            }
                        </strong>.
                        You'll continue to have Premium access until then.
                        </p>
                    </div>
                    )}

                    <div style={{
                    backgroundColor: 'rgba(139, 92, 246, 0.05)',
                    border: `2px solid ${theme.brand.primary}`,
                    padding: spacing.lg,
                    borderRadius: '8px',
                    marginBottom: spacing.md,
                    }}>
                    <p style={{
                        color: theme.text.primary,
                        marginBottom: spacing.md,
                        fontSize: typography.fontSize.base,
                    }}>
                        You're currently on the <strong>Premium plan</strong> ($5/month)
                    </p>
                    <ul style={{
                        paddingLeft: spacing.xl,
                        color: theme.text.secondary,
                        lineHeight: typography.lineHeight.relaxed,
                        marginBottom: spacing.lg,
                    }}>
                        <li>✓ Ad-free experience</li>
                        <li>✓ Enhanced AI responses (GPT-4.1 mini)</li>
                        <li>✓ Priority support</li>
                    </ul>

                    {userData?.subscription?.cancelAtPeriodEnd ? (
                        // Reactivate Subscription Button
                        <button 
                            onClick={ async () => {
                                try {
                                    setError('');
                                    const token = await user.getIdToken();
                                    await axios.post(
                                        `${process.env.REACT_APP_API_URL}/api/reactivate-subscription`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                    );

                                    sessionStorage.setItem('subscriptionReactivated', 'true');
                                    window.location.reload();
                                } catch (err) {
                                    setError('Failed to reactivate subscription')
                                }
                            }}
                            style={mergeStyles(
                                buttonStyles.base,
                                buttonStyles.primary
                            )}
                        >
                            Reactivate Premium
                        </button>
                    ) : (
                        // Cancel Subscription Button
                        !showCancelConfirm ? (
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                style={{
                                    padding: `${spacing.sm} ${spacing.lg}`,
                                    backgroundColor: 'transparent',
                                    border: `2px solid #ef4444`,
                                    color: '#ef4444',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: typography.fontSize.sm,
                                    fontWeight: typography.fontWeight.medium,
                                }}
                            >
                        Cancel Subscription
                            </button>
                    ) : (
                        <div style={{
                        backgroundColor: '#fef2f2',
                        border: '2px solid #ef4444',
                        padding: spacing.lg,
                        borderRadius: '8px',
                        }}>
                        <h3 style={{
                            fontSize: typography.fontSize.lg,
                            fontWeight: typography.fontWeight.semibold,
                            marginBottom: spacing.sm,
                            color: '#dc2626',
                        }}>
                            Cancel Your Subscription?
                        </h3>
                        <p style={{
                            color: theme.text.secondary,
                            marginBottom: spacing.md,
                            fontSize: typography.fontSize.sm,
                        }}>
                            You'll keep Premium access until the end of your current billing period. 
                            After that, your account will revert to the Free tier.
                        </p>
                        {error && (
                            <p style={{
                            color: '#ef4444',
                            marginBottom: spacing.md,
                            fontSize: typography.fontSize.sm,
                            }}>
                            {error}
                            </p>
                        )}
                        <div style={{ display: 'flex', gap: spacing.md }}>
                            <button
                            onClick={handleCancelSubscription}
                            disabled={isCancelling}
                            style={mergeStyles(
                                buttonStyles.base,
                                {
                                backgroundColor: '#dc2626',
                                color: 'white',
                                opacity: isCancelling ? 0.6 : 1,
                                cursor: isCancelling ? 'not-allowed' : 'pointer',
                                }
                            )}
                            >
                            {isCancelling ? 'Cancelling...' : 'Yes, Cancel Subscription'}
                            </button>
                            <button
                            onClick={() => {
                                setShowCancelConfirm(false);
                                setError('');
                            }}
                            disabled={isCancelling}
                            style={mergeStyles(
                                buttonStyles.base,
                                buttonStyles.secondary
                            )}
                            >
                            Keep Premium
                            </button>
                        </div>
                        </div>
                    ))}
                    </div>
                </section>
                ) : (
                // Upgrade Section for Free Users
                <section style={{ marginBottom: spacing['3xl'] }}>
                    <h2 style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.bold,
                    marginBottom: spacing.md,
                    color: theme.brand.primary,
                    }}>
                    Upgrade to Premium
                    </h2>

                    <div style={{
                    backgroundColor: 'rgba(139, 92, 246, 0.05)',
                    border: `2px solid ${theme.brand.primary}`,
                    padding: spacing.lg,
                    borderRadius: '8px',
                    marginBottom: spacing.md,
                    }}>
                    <p style={{
                        color: theme.text.primary,
                        marginBottom: spacing.md,
                        lineHeight: typography.lineHeight.relaxed,
                    }}>
                        Upgrade to Premium for $5/month and get:
                    </p>
                    <ul style={{
                        paddingLeft: spacing.xl,
                        color: theme.text.secondary,
                        lineHeight: typography.lineHeight.relaxed,
                    }}>
                        <li>Enhanced AI responses (GPT-4.1 mini model)</li>
                        <li>Power-user features</li>
                        <li>Priority support</li>
                    </ul>
                    </div>

                    <button
                    onClick={() => {
                        localStorage.setItem('showUpgradeModal', 'true');
                        window.location.href = '/';
                    }}
                    style={mergeStyles(buttonStyles.base, buttonStyles.primary)}
                    >
                    Upgrade to Premium
                    </button>
                </section>
                )}

                {/* Danger Zone - Delete Account */}
                <section style={{
                marginTop: spacing['3xl'],
                paddingTop: spacing['3xl'],
                borderTop: `2px solid ${theme.border.light}`,
                }}>
                <h2 style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.bold,
                    marginBottom: spacing.md,
                    color: '#ef4444',
                }}>
                    Danger Zone
                </h2>

                <div style={{
                    backgroundColor: '#fef2f2',
                    border: '2px solid #ef4444',
                    padding: spacing.lg,
                    borderRadius: '8px',
                    marginBottom: spacing.lg,
                }}>
                    <h3 style={{
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.semibold,
                    marginBottom: spacing.sm,
                    color: '#dc2626',
                    }}>
                    Delete Account
                    </h3>
                    <p style={{
                    color: theme.text.secondary,
                    marginBottom: spacing.md,
                    lineHeight: typography.lineHeight.relaxed,
                    }}>
                    Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <ul style={{
                    paddingLeft: spacing.xl,
                    color: theme.text.secondary,
                    marginBottom: spacing.md,
                    fontSize: typography.fontSize.sm,
                    }}>
                    <li>Your account will be deleted immediately</li>
                    <li>All your personal information will be removed</li>
                    <li>Your feedback submissions will be anonymized</li>
                    <li>If you have Premium, no refund will be provided for remaining time</li>
                    </ul>

                    {!showDeleteConfirm ? (
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        style={mergeStyles(
                        buttonStyles.base,
                        {
                            backgroundColor: '#ef4444',
                            color: 'white',
                        }
                        )}
                    >
                        Delete Account
                    </button>
                    ) : (
                    <div>
                        <p style={{
                        fontWeight: typography.fontWeight.semibold,
                        color: '#dc2626',
                        marginBottom: spacing.md,
                        }}>
                        Are you absolutely sure? This action cannot be undone.
                        </p>
                        <p style={{
                        color: theme.text.secondary,
                        marginBottom: spacing.sm,
                        fontSize: typography.fontSize.sm,
                        }}>
                        Type <strong>DELETE</strong> to confirm:
                        </p>
                        <input
                        type="text"
                        value={deleteInput}
                        onChange={(e) => setDeleteInput(e.target.value)}
                        placeholder="Type DELETE"
                        disabled={isDeleting}
                        style={{
                            width: '100%',
                            padding: spacing.md,
                            marginBottom: spacing.md,
                            border: `2px solid ${error ? '#ef4444' : theme.border.medium}`,
                            borderRadius: '8px',
                            fontSize: typography.fontSize.base,
                            fontFamily: typography.fontFamily.primary,
                        }}
                        />
                        {error && (
                        <p style={{
                            color: '#ef4444',
                            marginBottom: spacing.md,
                            fontSize: typography.fontSize.sm,
                        }}>
                            {error}
                        </p>
                        )}
                        <div style={{ display: 'flex', gap: spacing.md }}>
                        <button
                            onClick={handleDeleteAccount}
                            disabled={isDeleting}
                            style={mergeStyles(
                            buttonStyles.base,
                            {
                                backgroundColor: '#dc2626',
                                color: 'white',
                                opacity: isDeleting ? 0.6 : 1,
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                            }
                            )}
                        >
                            {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
                        </button>
                        <button
                            onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteInput('');
                            setError('');
                            }}
                            disabled={isDeleting}
                            style={mergeStyles(
                            buttonStyles.base,
                            buttonStyles.secondary
                            )}
                        >
                            Cancel
                        </button>
                        </div>
                    </div>
                    )}
                </div>
                </section>

                {/* Back to Home */}
                <div style={{
                    textAlign: 'center',
                    marginTop: spacing['3xl'],
                    marginBottom: spacing['3xl'],
                }}>
                <a 
                    href="/" 
                    style={{
                    color: theme.brand.primary,
                    textDecoration: 'none',
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.semibold,
                    }}
                >
                    ← Back to Judgy
                </a>
                </div>
            </div>
        </div>
    </>
  );
}